import { useEffect, useState } from "react";

/**
 * Fallback rendered by Sentry.ErrorBoundary when an unhandled render error
 * crashes the React tree. Matches the editorial 404 aesthetic (newspaper
 * voice, serif headline, dateline eyebrow) and surfaces the Sentry event id
 * so support can correlate visitor reports to backend traces.
 *
 * Intentionally inline-style-free so it renders even if Tailwind hasn't
 * applied yet — Tailwind class names are kept for the happy path but every
 * critical layout property is duplicated via style attributes.
 */
interface AppErrorFallbackProps {
  error: unknown;
  eventId: string | null;
  resetError: () => void;
}

export default function AppErrorFallback({ error, eventId, resetError }: AppErrorFallbackProps) {
  const [copied, setCopied] = useState(false);

  // Reset the copy badge after 1.5s so it doesn't stick across re-renders.
  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(id);
  }, [copied]);

  async function copyEventId() {
    if (!eventId) return;
    try {
      await navigator.clipboard.writeText(eventId);
      setCopied(true);
    } catch {
      /* clipboard unavailable — fall through. */
    }
  }

  const errorMessage = error instanceof Error ? error.message : null;

  return (
    <div
      className="min-h-screen bg-background"
      style={{ minHeight: "100vh", background: "var(--background, #fafaf7)" }}
    >
      <div
        className="mx-auto max-w-3xl px-5 py-16 md:py-24"
        style={{ maxWidth: 720, margin: "0 auto", padding: "64px 20px" }}
      >
        <p
          className="dateline mb-3"
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#666",
            marginBottom: 12,
          }}
        >
          Édition spéciale — incident
        </p>
        <h1
          className="font-serif text-4xl font-bold leading-tight text-foreground md:text-6xl"
          style={{
            fontFamily: '"Source Serif 4 Variable", "Source Serif Pro", Georgia, serif',
            fontWeight: 700,
            fontSize: "clamp(2rem, 5vw, 3.5rem)",
            lineHeight: 1.1,
            color: "#111",
            margin: 0,
          }}
        >
          La rédaction a fait une erreur.
        </h1>
        <p
          className="mt-4 max-w-2xl font-serif text-xl italic leading-relaxed text-muted-foreground"
          style={{
            fontFamily: '"Source Serif 4 Variable", Georgia, serif',
            fontStyle: "italic",
            fontSize: "1.25rem",
            color: "#444",
            marginTop: 16,
            maxWidth: 600,
          }}
        >
          Une page n'a pas pu être imprimée. L'incident a été signalé à notre
          équipe. Vous pouvez recharger ou revenir à l'accueil.
        </p>

        <hr
          className="my-10 border-border"
          style={{ margin: "40px 0", border: "none", borderTop: "1px solid #ddd" }}
        />

        <div
          className="rounded-lg border border-border bg-card p-5"
          style={{
            border: "1px solid #ddd",
            borderRadius: 8,
            background: "#fff",
            padding: 20,
          }}
        >
          <p
            className="dateline mb-2"
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#666",
              marginBottom: 8,
            }}
          >
            Référence à transmettre au support
          </p>
          {eventId ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <code
                style={{
                  fontFamily: "ui-monospace, SFMono-Regular, monospace",
                  fontSize: 13,
                  padding: "4px 8px",
                  background: "#f2f2f0",
                  borderRadius: 4,
                  color: "#111",
                }}
              >
                {eventId}
              </code>
              <button
                onClick={copyEventId}
                type="button"
                style={{
                  fontSize: 12,
                  padding: "4px 10px",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                {copied ? "Copié" : "Copier"}
              </button>
            </div>
          ) : (
            <p style={{ fontSize: 13, color: "#666" }}>
              Aucune référence Sentry disponible (Sentry n'est pas configuré dans cet environnement).
            </p>
          )}
          {errorMessage && (
            <p style={{ marginTop: 12, fontSize: 12, color: "#888", fontFamily: "ui-monospace, monospace" }}>
              {errorMessage}
            </p>
          )}
        </div>

        <div
          style={{
            marginTop: 32,
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            fontSize: 14,
          }}
        >
          <button
            type="button"
            onClick={() => {
              resetError();
              window.location.reload();
            }}
            style={{
              background: "#111",
              color: "#fff",
              border: "none",
              padding: "10px 18px",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Recharger la page
          </button>
          <a
            href="/"
            style={{
              padding: "10px 18px",
              border: "1px solid #ccc",
              borderRadius: 6,
              textDecoration: "none",
              color: "#111",
              fontWeight: 500,
            }}
          >
            Revenir à l'accueil
          </a>
          <a
            href="/contact?subject=support"
            style={{
              padding: "10px 18px",
              border: "1px solid #ccc",
              borderRadius: 6,
              textDecoration: "none",
              color: "#111",
              fontWeight: 500,
            }}
          >
            Contacter le support
          </a>
        </div>
      </div>
    </div>
  );
}
